import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, TrendingUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const DEFAULT_STATS = {
  yearsExperience:    { value: '15+',      label: 'Jahre Erfahrung' },
  successfulProjects: { value: '86+',      label: 'Erfolgreiche Projekte' },
  investmentVolume:   { value: 'CHF 97M+', label: 'Investitionsvolumen' },
};

export const Hero = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(DEFAULT_STATS);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(apiBase() + '/api/site/stats');
        const data = await r.json();
        if (alive && data?.success && data.stats) setStats({ ...DEFAULT_STATS, ...data.stats });
      } catch { /* keep defaults */ }
    })();
    return () => { alive = false; };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1519662978799-2f05096d3636"
          alt="Luxury Building"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-black/80" />
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#C4A55A]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#C4A55A]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 z-10 relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C4A55A]/10 border border-[#C4A55A]/30 mb-8 backdrop-blur-sm">
            <TrendingUp className="h-4 w-4 text-[#C4A55A]" />
            <span className="text-sm text-gray-200 font-medium">Premium Swiss Real Estate</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="text-white">{t.hero.title}</span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C4A55A] via-[#D4B56A] to-[#C4A55A]">
              {t.hero.titleHighlight}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            {t.hero.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={() => scrollToSection('aktienregister')}
              size="lg"
              className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold px-8 py-6 rounded-lg shadow-lg hover:shadow-[#C4A55A]/50 transition-all duration-300 hover:scale-105 group"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
            <Button
              onClick={() => scrollToSection('services')}
              size="lg"
              variant="outline"
              className="border-2 border-[#C4A55A] text-[#C4A55A] hover:bg-[#C4A55A] hover:text-black px-8 py-6 rounded-lg backdrop-blur-sm transition-all duration-300 hover:scale-105"
            >
              {t.hero.ctaSecondary}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto" data-testid="hero-stats">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#C4A55A] mb-2" data-testid="hero-stat-years">{stats.yearsExperience.value}</div>
              <div className="text-gray-400 text-sm">{stats.yearsExperience.label}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#C4A55A] mb-2" data-testid="hero-stat-projects">{stats.successfulProjects.value}</div>
              <div className="text-gray-400 text-sm">{stats.successfulProjects.label}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#C4A55A] mb-2" data-testid="hero-stat-volume">{stats.investmentVolume.value}</div>
              <div className="text-gray-400 text-sm">{stats.investmentVolume.label}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="w-6 h-10 border-2 border-[#C4A55A]/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-[#C4A55A] rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};